/**
 * Utility functions for accent-insensitive (diacritic-insensitive) search
 * @remarks
 * These functions normalize text by removing diacritical marks (accents)
 * to enable searching for players like "Vinícius Júnior" using "Vinicius Junior".
 *
 * Supported character transformations:
 * - Acute accents: á, é, í, ó, ú → a, e, i, o, u
 * - Grave accents: à, è, ì, ò, ù → a, e, i, o, u
 * - Circumflex: â, ê, î, ô, û → a, e, i, o, u
 * - Umlauts: ä, ë, ï, ö, ü → a, e, i, o, u
 * - Tildes: ã, ñ, õ → a, n, o
 * - Scandinavian: ø, å, æ → o, a, ae
 * - Cedilla: ç → c
 * - German ß → ss
 * - Other non-decomposing Latin letters: ł → l, đ/ð → d, þ → th, œ → oe
 *
 * The transliteration set mirrors PostgreSQL's `unaccent` extension (the
 * backend's `f_unaccent`), so client-side filtering does not drop rows the
 * backend's own predicate matched. Letters such as Ł, Ø and Æ carry no
 * combining mark, so NFD decomposition alone leaves them intact — they need
 * explicit rules (SE-6197).
 */

/**
 * Normalizes a string for search by removing diacritical marks and converting to lowercase
 * @param text - The text to normalize
 * @returns Normalized lowercase string with diacritics removed
 */
export const normalizeForSearch = (text: string): string => {
  return (
    text
      // Handle German ß first (before NFD decomposition)
      .replace(/ß/g, "ss")
      // Handle Scandinavian characters that don't decompose well
      .replace(/[Øø]/g, "o")
      .replace(/[Åå]/g, "a")
      .replace(/[Ææ]/g, "ae")
      // Other Latin letters with no combining mark to strip, so NFD leaves
      // them intact. Same mappings PostgreSQL's unaccent.rules applies.
      .replace(/[Łł]/g, "l")
      .replace(/[ĐđÐð]/g, "d")
      .replace(/[Þþ]/g, "th")
      .replace(/[Œœ]/g, "oe")
      // NFD decomposition separates base characters from combining diacritical marks
      .normalize("NFD")
      // Remove combining diacritical marks (Unicode range U+0300 to U+036F)
      .replace(/[\u0300-\u036f]/g, "")
      // Convert to lowercase
      .toLowerCase()
      // Trim whitespace
      .trim()
  );
};

/**
 * Checks if text contains the search query using accent-insensitive comparison
 * @param text - The text to search within
 * @param query - The search query
 * @returns True if the normalized text contains the normalized query
 */
export const searchIncludes = (text: string, query: string): boolean => {
  const normalizedText = normalizeForSearch(text);
  const normalizedQuery = normalizeForSearch(query);

  return normalizedText.includes(normalizedQuery);
};
